/**
 * What the client knows about a person's AI subscription connections, who may drive a session on a
 * subscription, and the words it shows them. Client-safe: no server imports.
 */
import type { CodeSessionPurpose, CodeSessionSource } from '@/generated/prisma/enums';
import type { FirewallRefusal } from '@/lib/ai/coding-agent-service/runtime/network-policy';

import type { CodeSessionAiSubscriptionModel } from '../models';
import type { AiSubscriptionProvider, AiSubscriptionProviderNames } from './providers';

import {
  CODE_SESSION_AI_SUBSCRIPTION_MODELS,
  CODE_SESSION_DEFAULT_MODEL,
  CODE_SESSION_OFFERED_MODELS,
  aiSubscriptionModelOf,
  codeSessionSpeedOf,
  isAiSubscriptionModel,
  isOfferedCodeSessionModel,
  splitStoredModelEffort,
  storedModelOf,
} from '../models';
import { AI_SUBSCRIPTION_DESCRIPTORS, AI_SUBSCRIPTION_PROVIDERS } from './providers';

/** A connected subscription as its person sees it. */
export type AiSubscriptionConnectionView =
  | {
      status: 'connected';
      /** The account's email at the provider, from its ID token. */
      email: string | null;
    }
  /** The provider refused the sign-in's tokens, or they ran out: the person signs in again. */
  | { status: 'needs_reconnect'; email: string | null };

/**
 * What settings and the /code model picker know about a person's subscription with one
 * provider.
 */
export type AiSubscriptionState = {
  /**
   * Where this person may connect and use it: the provider's rollout ∩ their /code installations.
   * Empty while the provider is off for them, when only disconnecting works.
   */
  installationIds: readonly number[];
  connection: AiSubscriptionConnectionView | null;
};

/** `GET /api/code-sessions/ai-subscriptions`: every provider's state. */
export type AiSubscriptionStates = Record<AiSubscriptionProvider, AiSubscriptionState>;

/** `POST /api/code-sessions/ai-subscriptions/[provider]/sign-in`: where to send the browser. */
export type StartAiSubscriptionSignInResponse = { authorizationUrl: string };

/**
 * `POST /api/code-sessions/ai-subscriptions/[provider]/sign-in/finish`: the subscription as it is
 * now connected.
 */
export type FinishAiSubscriptionSignInResponse = { state: AiSubscriptionState };

/** Whether a person's subscription can run a session in an installation now. */
export type AiSubscriptionUsability = 'usable' | 'disabled' | 'not_connected' | 'needs_reconnect';

/**
 * Whether a person's subscription can run a session: the provider's subscriptions are on in the
 * session's installation (its adapter's `rollout`), and the subscription is connected and active.
 */
export function aiSubscriptionUsability(input: {
  /** Whether the provider's subscriptions are on in the session's installation. */
  enabled: boolean;
  connection: AiSubscriptionConnectionView | null;
}): AiSubscriptionUsability {
  if (!input.enabled) {
    return 'disabled';
  }
  if (input.connection === null) {
    return 'not_connected';
  }
  return input.connection.status === 'connected' ? 'usable' : 'needs_reconnect';
}

/** Whether a person's subscription can run a session in `installationId`, from what the client knows. */
function aiSubscriptionUsabilityIn(
  state: AiSubscriptionState,
  installationId: number,
): AiSubscriptionUsability {
  return aiSubscriptionUsability({
    enabled: state.installationIds.includes(installationId),
    connection: state.connection,
  });
}

/**
 * The status the server refuses with while a person's subscription can't run a session: a conflict
 * when connecting it fixes that, else forbidden.
 */
export const AI_SUBSCRIPTION_UNUSABLE_STATUS: Record<
  Exclude<AiSubscriptionUsability, 'usable'>,
  403 | 409
> = {
  disabled: 403,
  not_connected: 409,
  needs_reconnect: 409,
};

/**
 * Why a session on an AI subscription refuses someone: the subscription isn't theirs, or its
 * owner's can't run it now.
 */
export type AiSubscriptionRefusal = 'not_owner' | Exclude<AiSubscriptionUsability, 'usable'>;

export type AiSubscriptionDriveDecision =
  | { ok: true }
  | { ok: false; reason: AiSubscriptionRefusal };

/**
 * Whether someone may make the agent of a session on an AI subscription run, or work in its VM:
 * only the subscription's owner may, the owner of its VM, which is the session's creator, or for
 * a side agent its main session's creator. An action that makes the agent run also needs the
 * owner's subscription usable now (`ownerSubscription`); one that only works in the VM, such as a
 * push or a setup rerun, leaves `ownerSubscription` out, since the owner may do it whatever their
 * subscription's state. The server's checks and the composer both decide here.
 */
export function aiSubscriptionDriveDecision(input: {
  /** Whether whoever acts owns the session's subscription. */
  actorIsOwner: boolean;
  /** The owner's subscription now, for an action that makes the agent run. */
  ownerSubscription?: AiSubscriptionUsability;
}): AiSubscriptionDriveDecision {
  if (!input.actorIsOwner) {
    return { ok: false, reason: 'not_owner' };
  }
  if (input.ownerSubscription === undefined || input.ownerSubscription === 'usable') {
    return { ok: true };
  }
  return { ok: false, reason: input.ownerSubscription };
}

/** The subscription as the provider's own copy calls it (`names.plan`). */
function subscriptionNameOf(provider: AiSubscriptionProvider): string {
  return AI_SUBSCRIPTION_DESCRIPTORS[provider].names.plan;
}

function sessionsUnavailable(subscriptionName: string): string {
  return `${subscriptionName} sessions aren’t available in this organization.`;
}

/**
 * What a session on an AI subscription tells someone it refuses, in the provider's words.
 * `ownerName` names the subscription's owner to someone else; null when their profile is gone.
 */
export function aiSubscriptionRefusalMessage(
  provider: AiSubscriptionProvider,
  reason: AiSubscriptionRefusal,
  ownerName: string | null,
): string {
  return refusalMessage(subscriptionNameOf(provider), reason, ownerName);
}

function refusalMessage(
  subscription: string,
  reason: AiSubscriptionRefusal,
  ownerName: string | null,
): string {
  switch (reason) {
    case 'not_owner':
      return ownerName === null
        ? `This session runs on its creator’s ${subscription}, so only they can send it messages or work in it.`
        : `This session runs on ${ownerName}’s ${subscription}, so only ${ownerName} can send it messages or work in it.`;
    case 'disabled':
      return sessionsUnavailable(subscription);
    case 'not_connected':
      return `Connect your ${subscription} to continue this session.`;
    case 'needs_reconnect':
      return `Reconnect your ${subscription} to continue this session.`;
  }
}

/**
 * Why a subscription hands out no token: the provider is off in the installation or the person
 * isn't in it, they never connected, must sign in again, or a refresh failed for now.
 */
export type AiSubscriptionWithheldReason =
  | 'disabled'
  | 'not_connected'
  | 'needs_reconnect'
  | 'unavailable';

/**
 * Why a subscription VM's firewall answers a request carrying the placeholder itself instead of
 * giving it the owner's token: the subscription hands out none (`AiSubscriptionWithheldReason`),
 * or the request isn't a model request (`out_of_scope`).
 */
export type AiSubscriptionFirewallRefusal = AiSubscriptionWithheldReason | 'out_of_scope';

/** A firewall refusal, and what the turn's error card offers for it. */
export type AiSubscriptionFirewallAnswer = FirewallRefusal & {
  /** The connect dialog the card opens, or none. */
  action: 'connect' | 'reconnect' | null;
  /** Whether sending the message again can work before something outside cubic changes. */
  retry: boolean;
};

/**
 * Where every subscription VM's firewall code starts, whatever its provider:
 * `cubic_ai_subscription_<provider>_<reason>`. `parseAiSubscriptionFirewallCode` reads the rest.
 */
export const AI_SUBSCRIPTION_FIREWALL_CODE_PREFIX = 'cubic_ai_subscription_';

/**
 * Each refusal's part of its code. `needs_reconnect` reads `reconnect`, which the app's checks and
 * the error cards expect.
 */
const FIREWALL_CODE_REASONS = {
  needs_reconnect: 'reconnect',
  not_connected: 'not_connected',
  disabled: 'disabled',
  unavailable: 'unavailable',
  out_of_scope: 'out_of_scope',
} satisfies Record<AiSubscriptionFirewallRefusal, string>;

/**
 * The code a `provider` subscription VM's firewall refuses with, by which the transcript
 * recognizes the subscription's own failures. The one place the codes are built.
 */
export function aiSubscriptionFirewallCode(
  provider: AiSubscriptionProvider,
  reason: AiSubscriptionFirewallRefusal,
): string {
  return `${AI_SUBSCRIPTION_FIREWALL_CODE_PREFIX}${provider}_${FIREWALL_CODE_REASONS[reason]}`;
}

/**
 * The provider and reason a firewall code names, the inverse of `aiSubscriptionFirewallCode` for
 * any provider, this bundle's or a newer deployment's; null for any other code.
 */
export function parseAiSubscriptionFirewallCode(
  code: string,
): { provider: string; reason: AiSubscriptionFirewallRefusal } | null {
  if (!code.startsWith(AI_SUBSCRIPTION_FIREWALL_CODE_PREFIX)) {
    return null;
  }
  const rest = code.slice(AI_SUBSCRIPTION_FIREWALL_CODE_PREFIX.length);
  for (const [reason, part] of Object.entries(FIREWALL_CODE_REASONS) as [
    AiSubscriptionFirewallRefusal,
    string,
  ][]) {
    if (rest.length > part.length + 1 && rest.endsWith(`_${part}`)) {
      return { provider: rest.slice(0, rest.length - part.length - 1), reason };
    }
  }
  return null;
}

/** A provider's firewall refusals, by reason. */
export type AiSubscriptionFirewallRefusals = Record<
  AiSubscriptionFirewallRefusal,
  AiSubscriptionFirewallAnswer
>;

/**
 * What a `provider` subscription VM's firewall answers, as JSON `{ error: { code, message } }`:
 * OpenCode shows `message` as the turn's error, and the transcript recognizes the subscription's
 * own failures by `code`. A 401 or 403, which OpenCode does not retry: the same configuration would
 * only refuse again.
 */
export function aiSubscriptionFirewallRefusals(
  provider: AiSubscriptionProvider,
): AiSubscriptionFirewallRefusals {
  const cards = aiSubscriptionFirewallCards(AI_SUBSCRIPTION_DESCRIPTORS[provider].names);
  const answer = (reason: AiSubscriptionFirewallRefusal, statusCode: 401 | 403) => ({
    statusCode,
    code: aiSubscriptionFirewallCode(provider, reason),
    ...cards[reason],
  });
  return {
    needs_reconnect: answer('needs_reconnect', 401),
    not_connected: answer('not_connected', 401),
    disabled: answer('disabled', 403),
    unavailable: answer('unavailable', 403),
    out_of_scope: answer('out_of_scope', 403),
  };
}

/**
 * What each firewall refusal tells the person, in the words of `names`, the fix the card offers
 * and whether Retry can work: the provider's names for its own refusals, and neutral ones for a
 * provider this bundle doesn't know (`errors.ts`).
 */
export function aiSubscriptionFirewallCards(
  names: Pick<AiSubscriptionProviderNames, 'plan' | 'vendor'>,
): Record<
  AiSubscriptionFirewallRefusal,
  Pick<AiSubscriptionFirewallAnswer, 'message' | 'action' | 'retry'>
> {
  return {
    needs_reconnect: {
      message: refusalMessage(names.plan, 'needs_reconnect', null),
      action: 'reconnect',
      retry: true,
    },
    not_connected: {
      message: refusalMessage(names.plan, 'not_connected', null),
      action: 'connect',
      retry: true,
    },
    disabled: { message: refusalMessage(names.plan, 'disabled', null), action: null, retry: false },
    unavailable: {
      message: `cubic couldn’t refresh your ${names.vendor} sign-in. Send your message again in a minute.`,
      action: null,
      retry: true,
    },
    // The same request is refused again.
    out_of_scope: {
      message: `cubic only uses your ${names.plan} for model requests.`,
      action: null,
      retry: false,
    },
  };
}

/**
 * Why a new session can't start on a subscription model: it isn't a general web session
 * (Slack, MCP, cubic's own sessions and setup sessions never run on a subscription); its starter
 * isn't a session on the same owner's subscription model; or the person's subscription can't run it.
 */
export type AiSubscriptionStartRefusal =
  | 'not_web'
  | 'invalid_starter'
  | Exclude<AiSubscriptionUsability, 'usable'>;

/** What a request to start a session on a subscription model is told when it may not. */
export function aiSubscriptionStartRefusalMessage(
  provider: AiSubscriptionProvider,
  reason: AiSubscriptionStartRefusal,
): string {
  const subscription = subscriptionNameOf(provider);
  switch (reason) {
    case 'not_web':
      return `Only general sessions in cubic Code can run on a ${subscription}.`;
    case 'invalid_starter':
      return `Only a session you started on your ${subscription} can start another session on it.`;
    case 'disabled':
      return sessionsUnavailable(subscription);
    case 'not_connected':
      return `Connect your ${subscription} to start a session on it.`;
    case 'needs_reconnect':
      return `Reconnect your ${subscription} to start a session on it.`;
  }
}

/**
 * Whether a new session from `source` for `purpose` may start on a subscription model: only a
 * general web session, started by a person or their agent. Decided before the subscription is read;
 * an agent-created one also needs the same owner and model as its starter, and all need the
 * creator's subscription usable now (`assertAiSubscriptionSessionStart`).
 */
export function mayStartOnAiSubscription(session: {
  source: CodeSessionSource;
  purpose: CodeSessionPurpose;
}): boolean {
  return session.source === 'web' && session.purpose === 'general';
}

/**
 * What the /code model picker offers under "Your <subscription>" for a new session, per provider:
 * one item that opens the connect dialog and selects nothing while the subscription needs
 * connecting, or the subscription's models.
 */
export type AiSubscriptionPickerGroup = { provider: AiSubscriptionProvider } & (
  | { kind: 'connect'; reconnect: boolean }
  | { kind: 'models'; models: readonly CodeSessionAiSubscriptionModel[] }
);

/**
 * The picker's group for `provider` in a new session in `installationId`, from the same state the
 * start check (`assertAiSubscriptionSessionStart`) reads for a session the person starts in the web
 * app: null while the provider's subscriptions are off there for them, a connect item while theirs
 * needs connecting, else the provider's models.
 */
export function aiSubscriptionPickerGroup(
  provider: AiSubscriptionProvider,
  state: AiSubscriptionState,
  installationId: number,
): AiSubscriptionPickerGroup | null {
  const subscription = aiSubscriptionUsabilityIn(state, installationId);
  switch (subscription) {
    case 'disabled':
      return null;
    case 'not_connected':
    case 'needs_reconnect':
      return { provider, kind: 'connect', reconnect: subscription === 'needs_reconnect' };
    case 'usable':
      return {
        provider,
        kind: 'models',
        models: CODE_SESSION_AI_SUBSCRIPTION_MODELS.filter(
          (entry) =>
            entry.provider === provider &&
            entry.speed === 'standard' &&
            isOfferedCodeSessionModel(entry),
        ),
      };
  }
}

/**
 * Every provider's group, in the registry's order, without the providers that offer nothing.
 * Empty until the person's subscriptions and the repository are known.
 */
export function aiSubscriptionPickerGroups(
  states: AiSubscriptionStates | undefined,
  installationId: number | undefined,
): readonly AiSubscriptionPickerGroup[] {
  if (states === undefined || installationId === undefined) {
    return [];
  }
  return AI_SUBSCRIPTION_PROVIDERS.flatMap((provider) => {
    const group = aiSubscriptionPickerGroup(provider, states[provider], installationId);
    return group === null ? [] : [group];
  });
}

/**
 * The model a new session starts on. With no choice from the person yet (`chosen` null), the
 * default: the first subscription model the picker offers in the selected repository, in the
 * registry's order, or cubic's default where it offers none. A subscription model the picker
 * doesn't offer there, as after a disconnect or a move to a repository where the provider's
 * subscriptions are off, gets that default too, which the picker shows instead, so nothing warms or
 * starts on a subscription the person cannot run there. A Gateway model the picker lists is kept,
 * even while a subscription is offered, and one it no longer lists, as a choice saved before the
 * model left the picker, gets the default too. A fast model, which the picker doesn't list, stays
 * while its standard model is offered, and gives way to that standard model once the model no
 * longer offers Fast mode. Either keeps the effort the choice names.
 */
export function aiSubscriptionStartModel(
  chosen: string | null,
  groups: readonly AiSubscriptionPickerGroup[],
): string {
  const offered = groups.flatMap((group) => (group.kind === 'models' ? group.models : []));
  const [first] = offered;
  const defaultModel = first === undefined ? CODE_SESSION_DEFAULT_MODEL : storedModelOf(first);
  if (chosen === null) {
    return defaultModel;
  }
  if (!isAiSubscriptionModel(chosen)) {
    const { model } = splitStoredModelEffort(chosen);
    return CODE_SESSION_OFFERED_MODELS.some((offered) => offered === model) ? chosen : defaultModel;
  }
  const speedChoice = codeSessionSpeedOf(chosen);
  const standard = speedChoice?.models.standard ?? chosen;
  const { model: standardModel } = splitStoredModelEffort(standard);
  if (!offered.some((entry) => storedModelOf(entry) === standardModel)) {
    return defaultModel;
  }
  return speedChoice?.models[speedChoice.speed] ?? standard;
}

export function aiSubscriptionFastModeHint(
  provider: AiSubscriptionProvider,
  session: 'new' | 'started',
): string {
  const cost = `Fast mode uses your ${AI_SUBSCRIPTION_DESCRIPTORS[provider].names.plan}’s limits faster.`;
  return session === 'new'
    ? cost
    : `${cost} Changes apply from your next message; a subagent already running keeps its speed.`;
}

/**
 * Whose subscription a session runs on, as `GET /api/code-sessions/:id` tells its viewer, beside
 * `mcpToolsRunAs`; null there for a session on cubic's Gateway. The session's model names the
 * provider.
 */
export type AiSubscriptionSessionView = {
  /**
   * Names the subscription's owner to anyone else; null for the owner, and when their name can't
   * be read.
   */
  ownerName: string | null;
  viewerIsOwner: boolean;
};

/**
 * What a session's viewer may do in it, as its page shows it: `open`, everything; `held`, the
 * owner writes but sends nothing until they connect their subscription again, which the banner
 * offers (`reconnect`: their connection is still there, but its sign-in ended); `closed`, the
 * viewer sends nothing here at all, because the session runs on someone else's subscription or the
 * provider's subscriptions are off in its organization. `message` is the server's refusal, in the
 * provider's words.
 */
export type AiSubscriptionSessionGate =
  | { kind: 'open' }
  | { kind: 'held'; provider: AiSubscriptionProvider; message: string; reconnect: boolean }
  | { kind: 'closed'; message: string };

const OPEN_GATE: AiSubscriptionSessionGate = { kind: 'open' };

/**
 * The gate of a session as its page knows it: what the server decides
 * (`aiSubscriptionDriveDecision`), from the session's read. Open for a session on cubic's Gateway
 * and for a model naming a provider this bundle doesn't know, while the page has not read whose
 * subscription the session runs on (`owner` undefined) and, for the owner, while their own
 * subscriptions load (`viewerStates`), when the server still decides. The viewer's own
 * subscription decides only for the owner.
 */
export function aiSubscriptionSessionGate(input: {
  /** The session's stored model, which names the provider; undefined while the page has not read it. */
  model: string | undefined;
  owner: AiSubscriptionSessionView | null | undefined;
  /** The session's installation. */
  installationId: number;
  viewerStates: AiSubscriptionStates | undefined;
}): AiSubscriptionSessionGate {
  const named = input.model === undefined ? undefined : aiSubscriptionModelOf(input.model);
  if (named === undefined || !input.owner) {
    return OPEN_GATE;
  }
  const { provider } = named;
  const decision = aiSubscriptionDriveDecision({
    actorIsOwner: input.owner.viewerIsOwner,
    ownerSubscription:
      input.owner.viewerIsOwner && input.viewerStates !== undefined
        ? aiSubscriptionUsabilityIn(input.viewerStates[provider], input.installationId)
        : undefined,
  });
  if (decision.ok) {
    return OPEN_GATE;
  }
  const message = aiSubscriptionRefusalMessage(provider, decision.reason, input.owner.ownerName);
  switch (decision.reason) {
    case 'not_owner':
    case 'disabled':
      return { kind: 'closed', message };
    case 'not_connected':
    case 'needs_reconnect':
      return { kind: 'held', provider, message, reconnect: decision.reason === 'needs_reconnect' };
  }
}

/** The button that opens the connect dialog, as the banner and the error cards label it. */
export function aiSubscriptionConnectLabel(
  provider: AiSubscriptionProvider,
  reconnect: boolean,
): string {
  return `${reconnect ? 'Reconnect' : 'Connect'} ${AI_SUBSCRIPTION_DESCRIPTORS[provider].names.vendor}`;
}
